// Premium-voice playback tuning — Sep 29 2026 (Randy — Windows clipping).
//
// Every knob that used to be a hardcoded guess inside useVoiceQA lives here,
// is adjustable from the Voice Lead-In Lab (Super Admin → Bump Studio), and
// can be overridden per device. The DEFAULTS reproduce the pre-Sep behaviour
// exactly (650ms silent lead-in + a route hold that stops the moment the
// first word sounds) — the Sep wake-dither / 200ms pump / 800ms lead stack
// is gone, because his own testing disproved the sleeping-speaker theory it
// was built on (desktop Chrome AND Edge clip on EVERY play; iPhone never).
//
// What each dial actually does:
//   leadMs          — how much lead-in is padded INTO the buffer ahead of the
//                     first word. Protects against an output route that starts
//                     flowing late and throws away whatever was due earlier.
//   ditherDb        — the level of that lead-in and of the route hold. -999 is
//                     pure digital silence; some output stacks ignore silence
//                     entirely, which is what a real (but inaudible) level is
//                     for. -45 dB is still far below anything audible at normal
//                     volume on a speaker.
//   routeHold       — keep one looping source on the output route from the tap
//                     until the first word sounds, so the route cannot park
//                     during a slow first synth.
//   scheduleAheadMs — start the buffer this far in the FUTURE instead of "now".
//                     A source scheduled in the past can be fast-forwarded by
//                     the render thread, which sounds exactly like losing the
//                     opening words mid-syllable.

const KEY = "mysilo_voice_tuning";

export const VOICE_TUNING_DEFAULTS = {
  leadMs: 650,
  ditherDb: -999,
  routeHold: true,
  scheduleAheadMs: 0,
};

export function getVoiceTuning() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { ...VOICE_TUNING_DEFAULTS };
    return { ...VOICE_TUNING_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...VOICE_TUNING_DEFAULTS };
  }
}

export function setVoiceTuning(patch) {
  const next = { ...getVoiceTuning(), ...(patch || {}) };
  try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* */ }
  return next;
}

export function resetVoiceTuning() {
  try { window.localStorage.removeItem(KEY); } catch { /* */ }
  return { ...VOICE_TUNING_DEFAULTS };
}

export function isVoiceTuningOverridden() {
  try { return !!window.localStorage.getItem(KEY); } catch { return false; }
}

// dBFS → linear amplitude. -999 (or anything below -200) means true silence.
export function tuningAmp(db) {
  const v = typeof db === "number" ? db : VOICE_TUNING_DEFAULTS.ditherDb;
  return v <= -200 ? 0 : Math.pow(10, v / 20);
}

// Fill the first `sampleCount` frames with a 60 Hz tone at `amp`. amp 0 leaves
// the buffer as created — digital silence.
function fillHoldSignal(buf, sampleCount, amp) {
  if (!amp) return buf;
  const n = Math.min(sampleCount || buf.length, buf.length);
  const w = (2 * Math.PI * 60) / buf.sampleRate;
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) data[i] = Math.sin(i * w) * amp;
  }
  return buf;
}

// One looping source that holds the output route open. Returns a stop function
// (or null when the dial is off).
export function startRouteHold(ctx, tuning) {
  const t = tuning || getVoiceTuning();
  if (!t.routeHold) return null;
  try {
    const buf = ctx.createBuffer(1, Math.max(1, Math.ceil(ctx.sampleRate * 0.5)), ctx.sampleRate);
    fillHoldSignal(buf, buf.length, tuningAmp(t.ditherDb));
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(ctx.destination);
    src.start();
    return () => {
      try { src.stop(); } catch { /* */ }
      try { src.disconnect(); } catch { /* */ }
    };
  } catch {
    return null;
  }
}

// Build (but do not start) the source for one decoded chunk.
// Returns { src, startAt, audibleDelayMs } — `audibleDelayMs` is how long after
// start() the first WORD actually sounds, so callers can line the follow-along
// scroll / scrubber playhead up with the audio instead of with the lead-in.
export function buildChunkSource(ctx, audioBuf, tuning, { withLead = false } = {}) {
  const t = tuning || getVoiceTuning();
  const leadMs = withLead ? Math.max(0, t.leadMs || 0) : 0;
  let buf = audioBuf;
  if (leadMs > 0) {
    const lead = Math.ceil(audioBuf.sampleRate * (leadMs / 1000));
    const padded = ctx.createBuffer(audioBuf.numberOfChannels, lead + audioBuf.length, audioBuf.sampleRate);
    fillHoldSignal(padded, lead, tuningAmp(t.ditherDb));
    for (let ch = 0; ch < audioBuf.numberOfChannels; ch++) {
      padded.getChannelData(ch).set(audioBuf.getChannelData(ch), lead);
    }
    buf = padded;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const aheadMs = Math.max(0, t.scheduleAheadMs || 0);
  return {
    src,
    startAt: ctx.currentTime + aheadMs / 1000,
    audibleDelayMs: leadMs + aheadMs,
  };
}

export function b64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
